import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { InfoOutlined, ContentCopy } from '@mui/icons-material';
import { useSectionStyles } from '@/styles/utilities/hooks';

const FMOSpecConfiguration = ({
  pocketspecs,
  inputspecs,
  selectedPocketspec,
  selectedInputspec,
  onPocketspecSelect,
  onInputspecSelect,
  onInspectInputspec,
  onCreateCustomInputspec
}) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();
  return (
    <>
      {/* TERMINAL CAPPING Section */}
      <Box sx={sectionStyles.sectionContainer}>
        <Typography variant="caption" sx={sectionStyles.sectionHeader}>
          TERMINAL CAPPING
        </Typography>
        
        {/* Container for pocket specs - matching other list styles */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: theme.spacing(0.5),
          backgroundColor: theme.palette.background.paper,
          p: theme.spacing(0.5)
        }}>
          <Box sx={{ display: 'flex', gap: theme.spacing(0.5) }}>
            {pocketspecs
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(spec => (
              <Card 
                key={spec.id_}
                elevation={0}
                sx={{ 
                  flex: 1,
                  border: '1px solid',
                  borderColor: selectedPocketspec === spec.id_ ? theme.palette.selection.border : theme.palette.divider,
                  backgroundColor: selectedPocketspec === spec.id_ ? theme.palette.selection.background : theme.palette.background.paper,
                  transition: 'all 0.2s'
                }}
              >
                <CardActionArea
                  onClick={() => onPocketspecSelect(spec.id_)}
                  sx={{ p: 0 }}
                >
                  <CardContent sx={{ p: theme.spacing(0.5), '&:last-child': { pb: theme.spacing(0.5) } }}>
                    <Typography variant="caption" sx={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, lineHeight: 1.2 }}>
                      {spec.name}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: theme.typography.fontSize.xs, color: theme.palette.text.secondary, lineHeight: 1.2, display: 'block' }}>
                      {spec.description || 'Terminal capping method'}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>

      {/* FRAGMENTATION & GAMESS CONFIGURATION Section */}
      <Box sx={sectionStyles.sectionContainer}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={sectionStyles.sectionHeader}>
            CONFIGURATION
          </Typography>
          <Button
            size="small"
            startIcon={<ContentCopy sx={{ fontSize: theme.dimensions.icon.small }} />}
            onClick={onCreateCustomInputspec}
            sx={{
              minWidth: 'auto',
              px: theme.spacing(1),
              py: 0,
              height: 'auto',
              fontSize: theme.typography.fontSize.xs,
              textTransform: 'none',
              fontWeight: theme.typography.fontWeight.medium,
              lineHeight: 1,
              '& .MuiButton-startIcon': {
                marginRight: theme.spacing(0.5),
                marginLeft: 0
              }
            }}
          >
            Create Custom
          </Button>
        </Box>
        
        {/* Grid layout for multiple input specs */}
        <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.spacing(0.5),
        maxHeight: theme.spacing(22.5),
        overflow: 'auto',
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: theme.spacing(0.5),
        p: theme.spacing(0.5),
        backgroundColor: theme.palette.background.paper
      }}>
        {inputspecs
          .sort((a, b) => {
            // Define basis order priority
            const basisOrder = [
              'STO-3G', '3-21G', '6-31G', '6-31G*', '6-31G**', 
              '6-31++G**', '6-311G*', '6-311G**'
            ];
            
            // Get basis indices (use high number if not in list)
            const aBasisIndex = basisOrder.indexOf(a.basis) !== -1 ? 
              basisOrder.indexOf(a.basis) : 999;
            const bBasisIndex = basisOrder.indexOf(b.basis) !== -1 ? 
              basisOrder.indexOf(b.basis) : 999;
            
            // First sort by basis
            if (aBasisIndex !== bBasisIndex) {
              return aBasisIndex - bBasisIndex;
            }
            
            // Then sort by name (FMO before FMO/HI-VQE)
            const aIsHIVQE = a.name.includes('HI-VQE');
            const bIsHIVQE = b.name.includes('HI-VQE');
            
            if (aIsHIVQE !== bIsHIVQE) {
              return aIsHIVQE ? 1 : -1; // FMO comes before FMO/HI-VQE
            }
            
            // Finally, alphabetical sort within same category
            return a.name.localeCompare(b.name);
          })
          .map(spec => (
          <Card 
            key={spec.id_}
            elevation={0}
            sx={{ 
              border: '1px solid',
              borderColor: selectedInputspec === spec.id_ ? theme.palette.selection.border : theme.palette.divider,
              backgroundColor: selectedInputspec === spec.id_ ? theme.palette.selection.background : theme.palette.background.paper,
              transition: 'all 0.2s',
              position: 'relative'
            }}
          >
            <CardActionArea
              onClick={() => onInputspecSelect(spec.id_)}
              sx={{ p: 0 }}
            >
              <CardContent sx={{ p: theme.spacing(0.5), '&:last-child': { pb: theme.spacing(0.5) } }}>
                <Typography variant="caption" sx={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, lineHeight: 1.2 }}>
                  {spec.name}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: theme.typography.fontSize.xs, color: theme.palette.text.secondary, lineHeight: 1.2, display: 'block' }}>
                  {(() => {
                    const parts = [];
                    if (spec.basis) parts.push(spec.basis);
                    
                    const cuts = [];
                    if (spec.cut_tail) cuts.push('Tail');
                    if (spec.cut_glycine) cuts.push('Gly');
                    if (cuts.length > 0) parts.push(`Cut: ${cuts.join('+')}`);
                    
                    return parts.length > 0 ? parts.join(' • ') : (spec.description || 'GAMESS options');
                  })()}
                </Typography>
              </CardContent>
            </CardActionArea>
            {selectedInputspec === spec.id_ && (
              <IconButton
                size="small"
                onClick={(e) => onInspectInputspec(spec.id_, e)}
                sx={{ 
                  position: 'absolute',
                  top: theme.spacing(0.25),
                  right: theme.spacing(0.25),
                  p: theme.spacing(0.25),
                  backgroundColor: theme.palette.selection.backgroundLight,
                  '&:hover': {
                    backgroundColor: theme.palette.selection.background
                  }
                }}
              >
                <InfoOutlined sx={{ fontSize: theme.dimensions.icon.small }} />
              </IconButton>
            )}
          </Card>
        ))}
        </Box>
      </Box>
    </>
  );
};

export default FMOSpecConfiguration;